import React, { useEffect, useState } from "react";
import { ActivityCard, MixedText, PlayButton, ProgressPill } from "../shared";

/**
 * GroupMerge — "Three Make a Group" for the plural present-tense lesson.
 *
 * Each row holds three singular sentences and a pronoun button (هُمْ /
 * أَنْتُمْ / نَحْنُ). Tapping a member plays its sentence; once all three
 * are heard the pronoun button unlocks, and tapping it folds the row into
 * the one plural sentence — the verb's front highlighted in gold and its
 * ونَ tail in violet — and plays it. Tapping the pronoun again unmerges
 * for comparison. Completes when every row has been merged once.
 */
function MergedSentence({ row }) {
  const { merged_ar: text, verb, prefix, suffix } = row;
  const idx = text.indexOf(verb);
  if (idx === -1) return <>{text}</>;
  const start = verb.startsWith(prefix) ? prefix : "";
  const end = suffix && verb.endsWith(suffix) ? suffix : "";
  const stem = verb.slice(start.length, verb.length - end.length);
  return (
    <>
      {text.slice(0, idx)}
      <span style={{ color: "var(--gold-bright)" }}>{start}</span>
      {stem}
      {end && <span style={{ color: "var(--tag-violet)" }}>{end}</span>}
      {text.slice(idx + verb.length)}
    </>
  );
}

export default function GroupMerge({ data, onComplete, audio }) {
  const [heard, setHeard] = useState(() => new Set());
  const [merged, setMerged] = useState(() => new Set());
  const [everMerged, setEverMerged] = useState(() => new Set());

  const done = everMerged.size >= data.rows.length;

  useEffect(() => {
    if (done) onComplete();
  }, [done, onComplete]);

  const handleMember = (member) => {
    setHeard((prev) => {
      const next = new Set(prev);
      next.add(member.id);
      return next;
    });
    if (audio) audio.play(member.ar);
  };

  const handleMerge = (row) => {
    const isMerged = merged.has(row.id);
    setMerged((prev) => {
      const next = new Set(prev);
      if (isMerged) next.delete(row.id);
      else next.add(row.id);
      return next;
    });
    if (!isMerged) {
      setEverMerged((prev) => {
        const next = new Set(prev);
        next.add(row.id);
        return next;
      });
    }
    if (audio && !isMerged) audio.play(row.merged_ar);
  };

  return (
    <ActivityCard>
      <div className="flex justify-end mb-3">
        <ProgressPill>{everMerged.size}/{data.rows.length} merged</ProgressPill>
      </div>

      <div className="flex flex-col gap-3">
        {data.rows.map((row) => {
          const isMerged = merged.has(row.id);
          const seen = everMerged.has(row.id);
          const ready = row.members.every((m) => heard.has(m.id));
          return (
            <div
              key={row.id}
              className="px-4 py-3.5 rounded-xl"
              style={{
                background: isMerged ? "var(--gold-bg)" : "var(--ink)",
                border: seen ? "1px solid var(--gold-dim)" : "1px solid var(--panel-line)",
              }}
            >
              {isMerged ? (
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => handleMerge(row)}
                    aria-pressed
                    className="shrink-0 rounded-lg px-3 py-2 transition-all hover:brightness-110"
                    style={{ background: "var(--gold)", border: "1px solid var(--gold)", color: "var(--ink)" }}
                  >
                    <span lang="ar" dir="rtl" className="font-arabic text-2xl leading-[1.4] block">{row.pronoun_ar}</span>
                    <span className="text-[10px] block">{row.pronoun_en}</span>
                  </button>
                  <div className="flex-1 min-w-0">
                    <p dir="rtl" lang="ar" className="font-arabic text-3xl leading-[1.7] m-0" style={{ color: "var(--parchment)" }}>
                      <MergedSentence row={row} />
                    </p>
                    <p className="text-sm mt-0.5 mb-0" style={{ color: "var(--parchment-dim)" }}>{row.merged_en}</p>
                    {row.note && (
                      <p className="text-sm mt-1.5 mb-0 px-3 py-1.5 rounded-lg inline-block" style={{ background: "var(--violet-bg)", color: "var(--parchment)" }}>
                        <MixedText text={row.note} />
                      </p>
                    )}
                  </div>
                  {audio && <PlayButton text={row.merged_ar} playingText={audio.playingText} onPlay={audio.play} />}
                </div>
              ) : (
                <div className="flex items-stretch gap-3">
                  <button
                    type="button"
                    onClick={ready ? () => handleMerge(row) : undefined}
                    disabled={!ready}
                    aria-pressed={false}
                    title={ready ? "Merge into one" : `Listen to all ${row.members.length} first`}
                    className={`shrink-0 self-center rounded-lg px-3 py-2 transition-all ${ready ? "hover:brightness-110 cursor-pointer" : "cursor-not-allowed opacity-50"}`}
                    style={{ background: "var(--ink)", border: "1px solid var(--gold-dim)", color: "var(--gold-bright)" }}
                  >
                    <span lang="ar" dir="rtl" className="font-arabic text-2xl leading-[1.4] block">{row.pronoun_ar}</span>
                    <span className="text-[10px] block" style={{ color: "var(--parchment-dim)" }}>{row.pronoun_en}</span>
                  </button>
                  <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                    {row.members.map((member) => {
                      const memberHeard = heard.has(member.id);
                      return (
                        <button
                          key={member.id}
                          type="button"
                          onClick={() => handleMember(member)}
                          className="flex items-center justify-between gap-3 px-3 py-1.5 rounded-lg text-left transition-all hover:brightness-110"
                          style={{
                            background: "var(--ink)",
                            border: memberHeard ? "1px solid var(--tag-violet)" : "1px solid var(--panel-line)",
                          }}
                        >
                          <span className="min-w-0">
                            <span dir="rtl" lang="ar" className="font-arabic text-xl leading-[1.7] block" style={{ color: "var(--parchment)" }}>
                              {member.ar}
                            </span>
                            <span className="text-xs block" style={{ color: "var(--parchment-dim)" }}>{member.en}</span>
                          </span>
                          {memberHeard && <span aria-hidden="true" style={{ color: "var(--tag-violet)" }}>✓</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {done && (
        <div
          className="flex items-start gap-2.5 mt-4 px-4 py-3 rounded-xl"
          style={{ background: "var(--violet-bg)", border: "1px solid var(--panel-line)" }}
        >
          <span className="text-lg leading-none" aria-hidden="true">{data.icon || "👨‍👩‍👧‍👦"}</span>
          <p className="text-sm leading-relaxed m-0" style={{ color: "var(--parchment)" }}>
            <MixedText text={data.takeaway} />
          </p>
        </div>
      )}
    </ActivityCard>
  );
}
